import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { TickerKind } from '../api'
import { KIND_PALETTE } from '../palette'

const CHIP_SIZE = {
    // The conveyor card's chip.
    card: 'gap-1.5 rounded-[20px] py-1 pl-[9px] pr-2.5 text-[11px] [&_svg]:h-3 [&_svg]:w-3',
    // The panel's aligned first column, and the dropdown legend: fixed width, centred.
    row: 'w-full justify-center gap-[5px] rounded-[20px] py-[3px] text-[10px] whitespace-nowrap [&_svg]:h-[11px] [&_svg]:w-[11px]',
    legend: 'w-full justify-center rounded-[20px] py-0.5 text-[10px] whitespace-nowrap',
} as const

export function KindChip({ kind, size, className }: { kind: TickerKind; size: keyof typeof CHIP_SIZE; className?: string }) {
    const { t } = useTranslation()
    const pal = KIND_PALETTE[kind]
    const Icon = pal.icon
    return (
        <span className={cn('relative inline-flex flex-shrink-0 items-center font-semibold', pal.chip, CHIP_SIZE[size], className)}>
            {size !== 'legend' && <Icon aria-hidden strokeWidth={2.4} className="flex-shrink-0" />}
            {t(`ticker.kinds.${kind}.chip`)}
        </span>
    )
}

/** The author's initials in the kind's colours; the kind's icon when no one wrote it (an RTA move). */
export function KindAvatar({ kind, initials, size }: { kind: TickerKind; initials: string | null; size: 'card' | 'row' }) {
    const pal = KIND_PALETTE[kind]
    const Icon = pal.icon
    return (
        <span
            aria-hidden
            className={cn(
                'relative flex flex-shrink-0 items-center justify-center rounded-full font-semibold',
                pal.chip,
                size === 'card' ? 'h-[30px] w-[30px] text-[11px]' : 'h-7 w-7 text-[10px]',
            )}
        >
            {initials ? initials : <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />}
        </span>
    )
}

/**
 * "Sample": a message written by the development-only "Populate ticker", not by a recovery. The
 * kind chip's shape, in neutral colours and a dashed edge, so it reads as a label on the message
 * rather than a seventh kind.
 */
export function SampleChip({ size, className }: { size: 'card' | 'row'; className?: string }) {
    const { t } = useTranslation()
    return (
        <span
            data-ticker-sample
            title={t('ticker.samples.markerLabel')}
            className={cn(
                'relative inline-flex flex-shrink-0 items-center rounded-[20px] border border-dashed border-muted-foreground/40 font-semibold text-muted-foreground',
                size === 'card' ? 'px-2 py-[3px] text-[10px]' : 'px-1.5 py-px text-[9.5px]',
                className,
            )}
        >
            {t('ticker.samples.marker')}
        </span>
    )
}
